import React, { useState, useEffect, useRef } from 'react';

/**
 * Animated progress indicator for spec analysis.
 * Shows a progress bar that advances through stages with rotating status messages.
 * Gives users confidence that work is happening during the 10-30s Claude call.
 */

const STAGES = [
  { label: 'Uploading document…', pct: 10, durationMs: 1500 },
  { label: 'Extracting text content…', pct: 25, durationMs: 3000 },
  { label: 'Analyzing project structure…', pct: 45, durationMs: 5000 },
  { label: 'Identifying tech stack & services…', pct: 60, durationMs: 5000 },
  { label: 'Extracting features & requirements…', pct: 75, durationMs: 5000 },
  { label: 'Generating project configuration…', pct: 88, durationMs: 8000 },
  { label: 'Finalizing…', pct: 94, durationMs: 10000 },
];

interface SpecAnalysisProgressProps {
  filename?: string;
}

export const SpecAnalysisProgress: React.FC<SpecAnalysisProgressProps> = ({ filename }) => {
  const [stageIdx, setStageIdx] = useState(0);
  const [barWidth, setBarWidth] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Immediately show first stage
    setBarWidth(STAGES[0].pct);

    const advance = (idx: number) => {
      if (idx >= STAGES.length - 1) return; // Stay on last stage until done
      timerRef.current = setTimeout(() => {
        const next = idx + 1;
        setStageIdx(next);
        setBarWidth(STAGES[next].pct);
        advance(next);
      }, STAGES[idx].durationMs);
    };

    advance(0);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const stage = STAGES[stageIdx];

  return (
    <div style={{ width: '100%', padding: '4px 0' }}>
      {/* Status message */}
      <div style={{
        fontSize: 13,
        color: 'var(--orange, #00E5FF)',
        fontWeight: 600,
        marginBottom: 8,
        transition: 'opacity 200ms ease',
      }}>
        {stage.label}
      </div>

      {/* Progress bar track */}
      <div style={{
        width: '100%',
        height: 6,
        background: 'rgba(255, 255, 255, 0.06)',
        borderRadius: 3,
        overflow: 'hidden',
        marginBottom: 8,
      }}>
        {/* Progress bar fill */}
        <div style={{
          height: '100%',
          width: `${barWidth}%`,
          background: 'linear-gradient(90deg, var(--orange, #00E5FF), #fb923c)',
          borderRadius: 3,
          transition: 'width 1.2s ease-out',
        }} />
      </div>

      {/* Filename + elapsed hint */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        {filename && (
          <div style={{ fontSize: 11, color: 'var(--text-dim, #737373)' }}>
            {filename}
          </div>
        )}
        <div style={{ fontSize: 11, color: 'var(--text-dim, #737373)' }}>
          This usually takes 10–20 seconds
        </div>
      </div>
    </div>
  );
};
